<script lang="ts" setup>
// Composables
import { useTeamLimits } from "@/composables/team/useTeamLimits";

// Components
import Popover from "./PopoverComponent.vue";
import Icon from "@/components/core/IconCore.vue";
import InfoTooltip from "@/components/core/InfoTooltip.vue";

// Assets
import enhanceIcon from "@/assets/svg/icons/enhance.svg?url";

defineProps<{
  labelText: string;
  badge?: string;
  size?: "sm" | "md";
  tooltip?: string;
  optional?: boolean;
  required?: boolean;
  enhance?: boolean;
  loading?: boolean;
  disabled?: boolean;
  icon?: string;
  placement?:
    | "top"
    | "top-start"
    | "top-end"
    | "bottom"
    | "bottom-start"
    | "bottom-end"
    | "left"
    | "left-start"
    | "left-end"
    | "right"
    | "right-start"
    | "right-end";
}>();

const emit = defineEmits(["update:enhance"]);

const { teamHasProjectsAccess } = useTeamLimits();

const handleEnhance = () => {
  emit("update:enhance");
};
</script>

<template>
  <label :class="['main-label', { 'main-label--disabled': disabled, size }]" :for="labelText">
    <Icon v-if="icon" :icon-src="icon" />

    <span class="semi-bold">
      {{ labelText }}
      <span v-if="optional" class="main-label__optional">(opcional)</span>
      <span v-if="required" class="main-label__required">*</span>
    </span>

    <span v-if="badge" class="main-label__badge">{{ badge }}</span>

    <InfoTooltip v-if="tooltip || $slots.children" :tooltip="tooltip" :placement="placement">
      <template v-if="$slots.children" #children>
        <slot name="children" />
      </template>
    </InfoTooltip>

    <slot />

    <Popover
      arrow
      content="Clique para obter uma proposta de preenchimento baseada no treinamento do projeto."
    >
      <template #reference>
        <div
          v-if="enhance && teamHasProjectsAccess"
          :class="['main-label__enhance', { 'main-label__enhance--loading': loading }]"
          @click="handleEnhance"
        >
          <template v-if="!loading">
            <Icon :icon-src="enhanceIcon" class="enhance" />
            Sugestão da IA
          </template>
          <span v-else class="spinner-border spinner-border-sm align-middle" />
        </div>
      </template>
    </Popover>
  </label>
</template>

<style lang="scss" scoped>
.main-label {
  @include flex(row, center);
  margin-bottom: $spacing_ultra_xs;
  gap: $spacing_ultra_xs;

  &--disabled {
    cursor: not-allowed;
  }

  &__required {
    color: $new-red_7 !important;
  }

  &__optional {
    font-size: $fs_ssm;
    color: $neutral_8;
    font-weight: $fw_md;
  }

  &__badge {
    background: $neutral_3;
    color: $primary_6;
    padding: 6px;
    border-radius: 3px;
    font-size: $fs_xs;
    font-weight: $fw_emd;
  }

  &__enhance {
    @include flex-center;
    color: $new-primary_7;
    padding-inline: $spacing_sm;
    margin-left: auto;
    border-radius: $br_sm;
    font-size: $fs_sm;
    font-weight: $fw_emd;
    cursor: pointer;

    &--loading {
      pointer-events: none;
    }
  }

  .semi-bold {
    color: $new-neutral_12;
    font-size: $fs_sm;
    font-weight: $fw_emd;
  }

  &.md .semi-bold {
    font-size: $fs_sm;
  }

  &.sm .semi-bold {
    font-size: $fs_ssm;
  }
}
</style>
